import React from 'react';
import { Card, Container, HeaderText, BodyText, Form } from 'shared-portal-component';
import { z } from 'zod';
import { useSelector } from 'react-redux';

import TwoSlot from '@/components/molecules/TwoSlot/TwoSlot';
import { setPasswordThunk } from '@/store/thunks/auth/setPasswordThunk';
import { registerMlKemKeys } from '@/store/thunks/auth/registerMlkemKeysThunk';
import { useAppDispatch } from '@/store';
import { selectRegistrationData } from '@/store/selectors/registration';

const MIN_CHARACTER_NUMBER = 12;

type FormValues = z.infer<typeof schema>;

const schema = z
  .object({
    password: z
      .string()
      .min(MIN_CHARACTER_NUMBER, 'Password must be at least 12 characters long')
      .regex(/[A-Z]/, 'Must contain at least 1 uppercase letter')
      .regex(/[a-z]/, 'Must contain at least 1 lowercase letter')
      .regex(/\d/, 'Must contain at least 1 number')
      .regex(/[^A-Za-z0-9]/, 'Must contain at least 1 special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const leftRequirements = [
  'At least 12 characters long',
  'At least 1 uppercase letter',
  'At least 1 lowercase letter',
];
const rightRequirements = ['At least 1 number', 'At least 1 special character'];

const fields = [
  {
    name: 'password',
    label: 'Password',
    placeholder: 'Enter a new password',
    required: true,
    variant: 'outline' as const,
    type: 'password',
  },
  {
    name: 'confirmPassword',
    label: 'Confirm Password',
    placeholder: 'Re-enter your password to confirm it',
    required: true,
    variant: 'outline' as const,
    type: 'password',
  },
];

interface PasswordFormProps {
  formData?: { password?: string; confirmPassword?: string };
  onNext: (values: { password: string; confirmPassword: string }) => void;
  loading?: boolean;
  error?: string;
}

const PasswordForm: React.FC<PasswordFormProps> = ({ formData, onNext, loading, error }) => {
  const dispatch = useAppDispatch();
  const {username} = useSelector(selectRegistrationData);
  return(
  <Container fluid>
    <Card>
      <HeaderText text="Configure your password" size="h2" className="header-text-auth" />
      <BodyText
        text="Please create and confirm a password for your administrator account. Passwords must be:"
        color="#000"
        className="body-text-auth"
        as="p"
      />

      {/* Use TwoSlot here for requirements */}
      <TwoSlot>
        <TwoSlot.FirstSlot>
            {leftRequirements.map((req) => (
              <BodyText as="p" text={req} key={req} />
            ))}
        </TwoSlot.FirstSlot>
        <TwoSlot.SecondSlot>
            {rightRequirements.map((req) => (
              <BodyText as="p" text={req} key={req} />
            ))}
        </TwoSlot.SecondSlot>
      </TwoSlot>
      <Form schema={schema} fields={fields}  onSubmit={(values: FormValues) => {
        const body = {username : username ?? '', password: values.password }
                void dispatch(setPasswordThunk(body)).then(async(action) => {
                  if (setPasswordThunk.fulfilled.match(action)) {
                    const mlkemAction = await dispatch(registerMlKemKeys({ password: values.password }));
                    if (registerMlKemKeys.rejected.match(mlkemAction)) {
                      return;
                    }
                    onNext(values);
                  }
                });
              }} />
    </Card>
  </Container>
);}

export default PasswordForm;
